import { Link } from "react-router-dom";

const ImgPlaceholder = ({
  tone = "light",
  className = "",
}: {
  tone?: "light" | "mid" | "dark";
  className?: string;
}) => {
  const bg =
    tone === "dark"
      ? "bg-[#3a3a38]"
      : tone === "mid"
      ? "bg-[#b8b2a6]"
      : "bg-[#dddad5]";
  return (
    <div
      className={`${bg} w-full h-full flex items-center justify-center ${className}`}
    >
      <svg
        width="40"
        height="40"
        viewBox="0 0 24 24"
        fill="none"
        stroke={tone === "dark" ? "#888" : "#a09c97"}
        strokeWidth="0.8"
        className="opacity-60"
      >
        <rect x="3" y="3" width="18" height="18" />
        <circle cx="8.5" cy="8.5" r="1.5" />
        <polyline points="21 15 16 10 5 21" />
      </svg>
    </div>
  );
};

const Navbar = () => (
  <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-[#e8e4de]">
    <div className="max-w-7xl mx-auto px-[18px] md:px-12 lg:px-20 h-[76px] md:h-[120px] flex items-center justify-between">
      <Link to="/" className="flex items-center flex-shrink-0 min-w-0">
        <img
          src="/logo.png"
          alt="Del Valle Landscaping"
          className="h-[50px] md:h-[104px] w-auto object-contain block"
          style={{ maxWidth: "260px" }}
        />
      </Link>
      <nav className="hidden md:flex items-center gap-10">
        <Link
          to="/"
          className="text-[10px] tracking-[0.22em] uppercase text-[#6b6560] hover:text-[#1A1A1A] transition-colors duration-200"
        >
          Home
        </Link>
        <a
          href="/#services"
          className="text-[10px] tracking-[0.22em] uppercase text-[#6b6560] hover:text-[#1A1A1A] transition-colors duration-200"
        >
          Services
        </a>
        <a
          href="/#about"
          className="text-[10px] tracking-[0.22em] uppercase text-[#6b6560] hover:text-[#1A1A1A] transition-colors duration-200"
        >
          About
        </a>
        <a
          href="/#contact"
          className="text-[10px] tracking-[0.22em] uppercase text-[#6b6560] hover:text-[#1A1A1A] transition-colors duration-200"
        >
          Contact
        </a>
      </nav>
      <a
        href="tel:7073276586"
        className="hidden md:inline-flex bg-[#2F3E2F] text-white text-[9px] tracking-[0.18em] uppercase px-5 py-2.5 hover:bg-[#253225] transition-colors duration-200"
      >
        Call / Text 707-327-6586
      </a>
    </div>
  </header>
);

const Footer = () => (
  <footer className="py-8 md:py-10 bg-white border-t border-[#e8e4de]">
    <div className="max-w-7xl mx-auto px-[24px] md:px-12 lg:px-20 flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left">
      <div className="flex items-center gap-3">
        <img
          src="/logo.png"
          alt="Del Valle Landscaping"
          className="h-8 md:h-9 w-auto object-contain"
        />
      </div>
      <p className="text-[10px] text-[#B8B2A6] tracking-widest">
        © {new Date().getFullYear()} Del Valle Landscaping · Sonoma County, CA · Lic. #07646
      </p>
      <a
        href="tel:7073276586"
        className="text-[11px] md:text-[10px] text-[#6b6560] tracking-widest hover:text-[#1A1A1A] transition-colors"
      >
        707-327-6586
      </a>
    </div>
  </footer>
);

export default function StructuralGroundWork() {
  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <Navbar />

      {/* ── HERO ─────────────────────────────────────────────────────── */}
      <section className="relative pt-[76px] md:pt-[120px] min-h-[80vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/sg-hero.jpg"
            alt="Structural ground work trench excavation"
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
        </div>
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(to top, rgba(20,28,20,0.92) 0%, rgba(47,62,47,0.75) 50%, rgba(47,62,47,0.50) 100%)",
          }}
        />
        <div className="relative z-10 w-full pb-20 pt-24">
          <div className="max-w-7xl mx-auto px-5 md:px-12 lg:px-20">
            <p className="text-[9px] sm:text-[10px] tracking-[0.32em] uppercase text-white/60 mb-5 font-light">
              Foundation · Del Valle Landscaping
            </p>
            <h1 className="font-serif text-[38px] sm:text-5xl md:text-6xl lg:text-[72px] font-medium leading-[1.08] text-white mb-6 tracking-[-0.01em] max-w-3xl">
              Structural &amp;{" "}
              <em className="italic font-normal">Ground Work</em>
            </h1>
            <p className="text-[15px] md:text-base text-white/70 leading-[1.9] max-w-md mb-10 font-light">
              The work that holds everything together. Retaining walls, grading,
              drainage, and excavation — engineered for the long term.
            </p>
            <a
              href="tel:7073276586"
              className="inline-flex bg-[#2F3E2F] text-white text-[10px] tracking-[0.22em] uppercase px-9 py-4 hover:bg-[#253225] transition-colors duration-300"
            >
              Call / Text 707-327-6586
            </a>
          </div>
        </div>
      </section>

      {/* ── PROJECT GALLERY ──────────────────────────────────────────── */}
      <section className="py-10 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-5 md:px-12 lg:px-20">
          <p className="text-[9px] md:text-[10px] tracking-[0.32em] uppercase text-[#B8B2A6] mb-6 font-light">
            Our Work
          </p>
          <div className="w-full mb-3 overflow-hidden" style={{ height: "clamp(260px, 52vw, 600px)" }}>
            <img
              src="/sg-gallery-main.jpg"
              alt="Utility trench with pipe installation"
              className="w-full h-full object-cover object-center"
            />
          </div>
          <div className="grid grid-cols-3 gap-3">
            {[
              { src: "/sg-gallery-1.jpg", alt: "Irrigation valve manifold installation" },
              { src: "/sg-gallery-2.jpg", alt: "Ground utility valve cluster near deck" },
              { src: "/sg-hero.jpg", alt: "Trench excavation with conduit work" },
            ].map((img, i) => (
              <div key={i} className="overflow-hidden" style={{ height: "clamp(100px, 18vw, 220px)" }}>
                <img
                  src={img.src}
                  alt={img.alt}
                  className="w-full h-full object-cover object-center"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── ABOUT THIS SERVICE ───────────────────────────────────────── */}
      <section className="py-8 md:py-16 bg-[#F4F1EA]">
        <div className="max-w-7xl mx-auto px-5 md:px-12 lg:px-20">
          <div className="max-w-2xl">
            <p className="text-[9px] md:text-[10px] tracking-[0.32em] uppercase text-[#B8B2A6] mb-5 font-light">
              About This Service
            </p>
            <h2 className="font-serif text-[30px] md:text-4xl lg:text-5xl font-medium text-[#1A1A1A] mb-8 tracking-[-0.01em] leading-[1.1]">
              Built From the{" "}
              <em className="italic font-normal">Ground Up</em>
            </h2>
            <div className="w-10 h-px bg-[#B8B2A6] mb-8" />
            <p className="text-base text-[#6b6560] leading-[1.9] font-light">
              Great outdoor spaces begin with sound ground work. Del Valle
              specializes in the foundational phase of construction — precise
              excavation, engineered retaining walls, proper drainage systems,
              and site grading that prevents erosion and water intrusion. This
              is the work most homeowners never see, but it is what separates a
              project that holds up for thirty years from one that fails in
              three.
            </p>
          </div>
        </div>
      </section>

      {/* ── FEATURES / WHAT WE DO ────────────────────────────────────── */}
      <section className="py-8 md:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-5 md:px-12 lg:px-20">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 md:gap-24">
            <div>
              <p className="text-[9px] md:text-[10px] tracking-[0.32em] uppercase text-[#B8B2A6] mb-5 font-light">
                What We Do
              </p>
              <h2 className="font-serif text-[28px] md:text-4xl font-medium text-[#1A1A1A] mb-10 tracking-[-0.01em] leading-[1.1]">
                Our Capabilities
              </h2>
              <ul className="space-y-5">
                {[
                  "Retaining wall construction",
                  "Site grading & leveling",
                  "Excavation & land clearing",
                  "French drain & drainage systems",
                  "Erosion control & slope stabilization",
                  "Concrete foundations & footings",
                  "Block wall & boulder installation",
                  "Utility trench & conduit work",
                ].map((item, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-4 text-sm text-[#6b6560] font-light leading-[1.8]"
                  >
                    <span className="mt-[9px] w-4 h-px bg-[#B8B2A6] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <p className="text-[9px] md:text-[10px] tracking-[0.32em] uppercase text-[#B8B2A6] mb-5 font-light">
                How We Work
              </p>
              <h2 className="font-serif text-[28px] md:text-4xl font-medium text-[#1A1A1A] mb-10 tracking-[-0.01em] leading-[1.1]">
                Our Process
              </h2>
              <div className="relative">
                <div className="absolute left-[23px] top-0 bottom-0 w-px bg-[#e4e0da]" />
                <div className="flex flex-col">
                  {[
                    {
                      num: "01",
                      title: "Consultation",
                      desc: "We evaluate the site's topography, drainage patterns, soil conditions, and project requirements.",
                    },
                    {
                      num: "02",
                      title: "Planning",
                      desc: "We engineer the drainage solution, wall design, or grading plan with materials, permits, and timeline.",
                    },
                    {
                      num: "03",
                      title: "Construction",
                      desc: "Excavation, structural build, drainage installation, backfill, and compaction — done with precision equipment.",
                    },
                    {
                      num: "04",
                      title: "Final Walkthrough",
                      desc: "We review every wall, grade, and drainage outlet with you to verify structural performance and finish.",
                    },
                  ].map((step, i) => (
                    <div
                      key={i}
                      className="relative flex gap-8 pb-10 last:pb-0"
                    >
                      <div className="relative z-10 flex-shrink-0 w-12 h-12 bg-white border border-[#e4e0da] flex items-center justify-center">
                        <span className="font-serif text-xs font-medium text-[#B8B2A6]">
                          {step.num}
                        </span>
                      </div>
                      <div className="pt-3">
                        <h3 className="text-[10px] font-medium tracking-[0.22em] uppercase text-[#1A1A1A] mb-2">
                          {step.title}
                        </h3>
                        <p className="text-sm text-[#6b6560] leading-[1.8] font-light">
                          {step.desc}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA SECTION ─────────────────────────────────────────────── */}
      <section className="py-12 md:py-20 bg-[#1A1A1A]">
        <div className="max-w-7xl mx-auto px-5 md:px-12 lg:px-20 text-center">
          <p className="text-[9px] md:text-[10px] tracking-[0.32em] uppercase text-[#B8B2A6] mb-6 font-light">
            Start Your Project
          </p>
          <h2 className="font-serif text-[34px] md:text-5xl lg:text-6xl font-medium text-white mb-8 tracking-[-0.01em] leading-[1.1]">
            Ready to Start Your Project?
          </h2>
          <p className="text-base text-[#9a958f] font-light leading-[1.8] mb-12 max-w-md mx-auto">
            Contact Lucio directly for a free on-site estimate. We respond the
            same day.
          </p>
          <a
            href="tel:7073276586"
            className="inline-flex bg-white text-[#1A1A1A] text-[10px] tracking-[0.22em] uppercase px-10 py-4 hover:bg-[#f0ece4] transition-colors duration-300"
          >
            Call / Text 707-327-6586
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
